import { formatMessage, randomMessage } from "model/message";
import Debug from "debug";
import City from "model/political/city";
import { standingChanged } from "lib/strings/city";
import clock from "util/clock";
const debug = Debug("action:beg");
const ActionType = "Beg";

const SUCCESS_RATE = 0.7;
const MAX_BEG_FOOD = 5;
const MAX_BEG_COINS = 3;

const STATUS_METRICS = {
  influence: 0.4,
  intimidation: -0.1,
  respect: -0.2,
};

// Adjust these values based on the city's generosity
const GENEROSITY_MULTIPLIERS = {
  [City.Generosity.Generous]: 1.5,
  [City.Generosity.Fair]: 1,
  [City.Generosity.Greedy]: 0.5,
};

/**
 * Begging is watched. Everything here reports the town's hands and the town's eyes — what goes
 * into the tin, and what the people doing it have decided about the man holding it.
 *
 * Two channels, and they are not the same length. The toast is the click: what landed in the
 * bowl this time, with the numbers behind it. The log line is the slow one — it shows on every
 * tenth point of charisma, or when the town's standing turns over, so it must read as a thing
 * noticed after a long while and never as a receipt.
 *
 * The tin is the dog's bowl from takeBowl.js. It is the same object all game and the lines say
 * so; nothing here upgrades it to a hat or a cup.
 */

const notificationText = ({ gain: { charisma } }, totalCharisma, newStatus) => {
  if ((!charisma || totalCharisma % 10 !== 0) && !newStatus) return;

  // The three standings are worded once, in lib/strings/city.js, beside the lines that read
  // the same standing off the street on arrival.
  if (newStatus) {
    const line = standingChanged(newStatus);
    if (line) return formatMessage(line);
  }

  // Charisma accruing is the town getting used to him. Every one of these used to name the
  // feeling outright — `suspicion wanes`, `trust builds`. The first two answer the suspicious
  // lines in lib/strings/city.js on purpose: the same street, later, doing the opposite thing.
  const message = randomMessage([
    "talk goes on when you pass now.",
    "a child looks at you and is not called back.",
    "they have stopped watching your hands.",
    "a man nods and keeps walking.",
    "the same face twice, and it does not turn away.",
  ]);
  return message;
};

// What was given, rather than that something was. The old success line was a coin flip between
// the bare word `a gift` and the charity level's description, which is written for the level-up
// modal in a wholly different register and repeats for every beg at that level.
//
// Keyed on the charity ladder's `act`, not its level. Ten registers would be ten pools each
// going stale inside the single level that used it; three turn over slowly enough to stay
// unfamiliar, and the division already exists — see the header of skills/charity/index.js. One
// is the street, two is the remedy, three is what arrives while he is not there.
//
// The bowl is in all three and does not change. It is the dog's bowl from takeBowl.js and the
// man carrying it at Shepherd is the man who was face down beside it in the first town. What
// changes is who puts something in it, and whether they wait to be seen doing it.
const emptyLines = {
  // Was `the bowl sits **empty**`. Bold is the resource deltas everywhere else in the log, so
  // bolding a word made `empty` read as a number.
  1: [
    "the bowl sits empty.",
    "a long while and nothing in the tin.",
    "boots pass and pass. the tin stays as it was.",
  ],
  2: [
    "no one comes. the bottles stay corked.",
    "they look at what is laid out and go on.",
    "a man asks the price and does not come back.",
  ],
  3: [
    "no one on the road. the bowl where i left it.",
    "nothing at the door. some days there is nothing.",
    "the bowl sits as it sat. nobody came in the night.",
  ],
};

const givenLines = {
  1: {
    both: [
      "bread and a coin, and neither one handed to you.",
      "they set down food and money and do not stop.",
      "bread in the tin and a coin under it.",
    ],
    coins: [
      "a coin rings in the tin.",
      "a coin drops in and the boots go on past.",
      "coin in the bowl. no face to go with it.",
    ],
    food: [
      "a heel of bread in the tin. no words with it.",
      "food set down in the dirt beside the bowl.",
      "bread, and the hand gone before you look up.",
    ],
  },
  2: {
    both: [
      "they pay what they have, bread and coin both, and take it away wrapped.",
      "food and money set down together. the bottle gone with them.",
      "a coin and what is left of a loaf. the cork goes back in.",
    ],
    coins: [
      "she pays for the small bottle and does not look at me.",
      "coin for a bottle. he is gone before the cork is back in.",
      "money in the bowl and one bottle lighter.",
    ],
    food: [
      "bread for the bottle. she counts it out like it was money.",
      "they bring what they have. it is food and it is not much.",
      "a sack set down. the bottle taken. no words.",
    ],
  },
  3: {
    both: [
      "bread and coin left in the night, and the bowl was not where i put it.",
      "food and money at the door. they come while i sleep now.",
      "the bowl is full and i did not see a hand near it.",
    ],
    coins: [
      "coin in the bowl before i sat down.",
      "money left in the night. no one waited to be thanked.",
      "a rider comes and goes and there is coin in the bowl.",
    ],
    food: [
      "food at the door in the morning. nobody waited to be seen.",
      "bread left on the step, still warm.",
      "a sack of meal against the wall and no one to ask after it.",
    ],
  },
};

const given = ({ food, coins }, act) => {
  const lines = givenLines[act] || givenLines[1];
  if (food && coins) return randomMessage(lines.both);
  if (coins) return randomMessage(lines.coins);
  return randomMessage(lines.food);
};

const messageText = ({ success, gain }, act) => {
  if (!success) {
    return formatMessage(randomMessage(emptyLines[act] || emptyLines[1]));
  }

  return formatMessage(given(gain, act), { gain });
};

const calculateGain = (max, progression, generosityMultiplier) => {
  const currentLevel = progression.getCurrentLevelIndex("charity");
  const levelBonus = Math.sqrt(currentLevel); // Use square root to moderate the level's impact
  return Math.floor(
    Math.random() * max * (1 + levelBonus) * generosityMultiplier,
  );
};
const calculateCharismaGain = (totalBegs, charismaValue) => {
  const baseGainFrequency = 2;
  const growthControl = 0.5;
  const charismaGainFrequency = Math.floor(
    Math.max(baseGainFrequency, charismaValue ** growthControl),
  );
  const adjustedFrequency = Math.min(charismaGainFrequency, 10); // maxFrequency

  return totalBegs % adjustedFrequency === 0 ? 1 : 0;
};

const attempt = (context) => {
  const {
    party: { history, traits, progression },
    political,
  } = context;

  let result = {
    success: false,
    gain: {
      food: 0,
      coins: 0,
      charisma: 0,
    },
    cost: {},
  };

  const generosityMultiplier =
    GENEROSITY_MULTIPLIERS[political.generosity(context.party.city)] || 1;
  debug(`Generosity: ${generosityMultiplier}`);
  const charismaBonus = (traits.charisma || 0) * 0.01; // Each point in charisma increases success rate by 1%
  const pathBonus = progression.getCurrentLevel("charity").bonus || 0;

  result.success =
    Math.random() <
      (SUCCESS_RATE + charismaBonus + pathBonus) * generosityMultiplier;

  if (!result.success) return result;

  result.gain.food = calculateGain(
    MAX_BEG_FOOD,
    progression,
    generosityMultiplier,
  );
  result.gain.coins = calculateGain(
    MAX_BEG_COINS,
    progression,
    generosityMultiplier,
  );
  result.gain.charisma = calculateCharismaGain(
    history.total(ActionType) + 1,
    traits.getTraitValue("charisma"),
  );

  result.success = result.gain.food > 0 || result.gain.coins > 0;
  return result;
};

const executeSideEffects = (context, result) => {
  const {
    calendar,
    political,
    party: { resources, traits, history, progression, city },
  } = context;

  // History
  history.add({ action: ActionType, result });

  // Update resources
  resources.delta(
    {
      food: result.gain.food,
      coins: result.gain.coins,
    },
    context,
  );
  traits.delta({ charisma: result.gain.charisma }, context);
  const { status, isNewStatus } = political.updateStatusMetrics(
    city,
    STATUS_METRICS,
  );

  // Update progression
  progression.addExperience("charity", 1, traits, context);

  // Assuming a 30% chance for a level-related event to occur
  let event = null;
  if (Math.random() < 0.3) {
    const level = progression.getCurrentLevel("charity");
    event =
      level.randomEvents[Math.floor(Math.random() * level.randomEvents.length)];
  }

  // Time passes
  // The player bought this phase. Rebaseline the clock so the accumulator, which may
  // already be nearly full, does not hand over a second phase moments later.
  calendar.nextStep(context);
  clock.reset();

  const message = messageText(
    result,
    progression.getCurrentLevel("charity").act,
  );
  const notification = notificationText(
    result,
    traits.getTraitValue("charisma"),
    isNewStatus ? status : null,
  );
  return { event, message, notification };
};

const beg = {
  title: ({ party: { progression } }) =>
    progression.getCurrentLevel("charity").actionLabel || "Beg",
  hotkey: "b",
  type: ActionType,
  enabled: () => true,
  level: ({ party }) => party.progression.getCurrentLevelIndex("charity"),
  execute: (context) => {
    const result = attempt(context);
    const { message, notification, event } = executeSideEffects(
      context,
      result,
    );
    debug(event);

    return { outcome: { event }, result: { message }, notification };
  },
};

export { ActionType as BegType, beg as default };
